/**
 * MOVIMIENTOS — «¿Qué pasó con mi dinero?»
 *
 * Se llamaba «Actividad» y era un extracto: cada fila una transacción, y una
 * transferencia entre cuentas propias aparecía como DOS filas, una roja de
 * −20,000 y una verde de +20,000, como si hubieras gastado y ganado el mismo
 * dinero el mismo día. Eso es el libro contable, no la historia del usuario.
 *
 * Aquí las piernas internas se agrupan en una sola operación conceptual
 * («Moviste dinero · Banreservas → Popular») y los filtros hablan en verbos.
 * La agrupación es de PRESENTACIÓN: debajo siguen siendo dos asientos.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/productTelemetry";
import { money, dayHeading, humanErrorLine } from "@/lib/human";
import { toast } from "sonner";
import { ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, CheckCircle2, Search, Sliders } from "lucide-react";
import EditTransactionDialog from "@/components/EditTransactionDialog";

// `kind` lo calcula el servidor (ledger.movement_kind) para que el cliente no
// tenga que re-derivar la regla — que es como las superficies se desincronizaron.
const FILTERS = [
  { key: "all", label: "Todo" },
  { key: "in", label: "Entró" },
  { key: "out", label: "Gasté" },
  { key: "move", label: "Moví" },
  { key: "pay", label: "Pagué" },
];

const GROUP_OF = {
  income: "in",
  purchase: "out", card_purchase: "out", transfer_fee: "out", recurring_payment: "out",
  transfer: "move",
  card_payment: "pay", debt_payment: "pay",
  balance_adjustment: "adjust",
};

const LOOK = {
  in: { icon: ArrowDownToLine, cls: "text-emerald-400", sign: "+" },
  out: { icon: ArrowUpFromLine, cls: "text-rose-400", sign: "−" },
  move: { icon: ArrowLeftRight, cls: "text-sky-300", sign: "" },
  pay: { icon: CheckCircle2, cls: "text-amber-300", sign: "−" },
  adjust: { icon: Sliders, cls: "text-slate-300", sign: "" },
};

export default function MovementsPage() {
  useEffect(() => { track("movements_viewed", { screen: "movements" }); }, []);
  const [txs, setTxs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [cards, setCards] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [editTx, setEditTx] = useState(null);
  // «Ver las compras de esta tarjeta» no es una pantalla nueva: es este mismo
  // libro con un filtro. Construir un módulo aparte habría duplicado el
  // renderizado de movimientos, que es justo donde nacen las divergencias.
  const [params] = useSearchParams();
  const cardFilter = params.get("card");

  const load = useCallback(async () => {
    try {
      const [t, a, c] = await Promise.all([
        api.get("/transactions", {
          params: { limit: 500, ...(cardFilter ? { card_id: cardFilter } : {}) } }),
        api.get("/accounts"),
        // Sin esto la fila decía «Tarjeta» a secas: sabías que gastaste, no con
        // cuál. Con dos tarjetas eso deja de ser un detalle.
        api.get("/cards"),
      ]);
      setTxs(t.data || []); setAccounts(a.data || []); setCards(c.data || []);
    } catch (e) { toast.error(humanErrorLine(e)); }
  }, [cardFilter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    window.addEventListener("td:data-changed", load);
    return () => window.removeEventListener("td:data-changed", load);
  }, [load]);

  const accName = useCallback(
    (id) => accounts.find(a => a.id === id)?.name || "", [accounts]);

  /** «Visa APAP», no «Tarjeta». El nombre es la única forma de saber en qué
   *  tarjeta cayó el gasto cuando hay más de una. */
  const cardName = useCallback((id) => {
    const c = cards.find(x => x.id === id);
    if (!c) return "Tarjeta";
    return c.last4 ? `${c.name} ••••${c.last4}` : c.name;
  }, [cards]);

  /**
   * Las dos piernas de una transferencia son una sola cosa para el usuario.
   * Se colapsan por `transfer_id`, quedándose con la de salida como
   * representante y anotando el destino. Las comisiones NO se colapsan: son
   * dinero que de verdad salió y merecen su propia línea.
   */
  const rows = useMemo(() => {
    const seenTransfer = new Map();
    const out = [];
    for (const t of txs) {
      const group = GROUP_OF[t.kind] || (t.type === "income" ? "in" : "out");
      if (t.kind === "transfer") {
        const prev = seenTransfer.get(t.transfer_id);
        if (prev) {
          if (t.type === "expense") { prev.from = accName(t.account_id); prev.amount = t.amount; prev.currency = t.currency; }
          else prev.to = accName(t.account_id);
          continue;
        }
        const row = {
          id: t.transfer_id, group: "move", title: "Moviste dinero",
          date: t.date, amount: t.amount, currency: t.currency, tx: null,
          from: t.type === "expense" ? accName(t.account_id) : null,
          to: t.type === "income" ? accName(t.account_id) : null,
        };
        seenTransfer.set(t.transfer_id, row);
        out.push(row);
        continue;
      }
      out.push({
        id: t.id, group, title: t.description || t.category, date: t.date,
        amount: t.amount, currency: t.currency, category: t.category,
        where: t.card_id ? cardName(t.card_id) : accName(t.account_id), tx: t,
      });
    }
    return out;
  }, [txs, accName, cardName]);

  const visible = rows.filter(r => {
    if (filter !== "all" && r.group !== filter) return false;
    if (!q) return true;
    const hay = `${r.title} ${r.category || ""} ${r.where || ""} ${r.from || ""} ${r.to || ""}`;
    return hay.toLowerCase().includes(q.toLowerCase());
  });

  // Agrupado por día, con encabezados en lenguaje humano.
  const byDay = useMemo(() => {
    const map = new Map();
    for (const r of visible) {
      const key = String(r.date || "").slice(0, 10);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }
    return [...map.entries()].sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [visible]);

  return (
    <div className="space-y-5 pb-4" data-testid="movements-page">
      <header className="flex items-start justify-between gap-3">
        <h1 className="text-2xl font-bold text-white">
          {cardFilter ? (cards.find(c => c.id === cardFilter)?.name || "Tarjeta") : "Movimientos"}
        </h1>
        <button onClick={() => window.dispatchEvent(new CustomEvent("td:quick"))} data-testid="movements-add"
          className="px-4 min-h-11 rounded-xl bg-white text-[#15141b] text-[14px] font-bold shrink-0">
          Anotar algo
        </button>
      </header>

      <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-1 px-1" role="tablist" aria-label="Filtrar movimientos">
        {FILTERS.map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)} role="tab"
            aria-selected={filter === f.key} data-testid={`movements-filter-${f.key}`}
            className={`px-4 min-h-11 rounded-full text-[14px] font-semibold whitespace-nowrap border transition-colors ${
              filter === f.key
                ? "bg-white text-[#15141b] border-white"
                : "bg-white/[0.04] text-slate-300 border-white/10 hover:bg-white/[0.08]"}`}>
            {f.label}
          </button>
        ))}
      </div>

      <label className="flex items-center gap-2.5 bg-white/[0.04] border border-white/10 rounded-2xl px-4 min-h-12">
        <Search className="w-4 h-4 text-slate-500 shrink-0" aria-hidden="true" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar"
          aria-label="Buscar en los movimientos" data-testid="movements-search"
          className="w-full bg-transparent text-white text-[15px] outline-none py-3" />
      </label>

      {byDay.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 p-8 text-center" data-testid="movements-empty">
          <p className="text-[16px] font-semibold text-white">
            {txs.length === 0 ? "Todavía no has anotado nada." : "Nada coincide con lo que buscas."}
          </p>
          {txs.length === 0 && (
            <>
              <p className="text-[14px] text-slate-400 mt-2 leading-relaxed max-w-sm mx-auto">
                Aquí va a aparecer todo lo que entra y sale de tu dinero, día por día.
              </p>
              <button onClick={() => window.dispatchEvent(new CustomEvent("td:quick"))} data-testid="movements-empty-cta"
                className="mt-5 px-5 min-h-12 rounded-2xl bg-white text-[#15141b] font-bold">
                Anotar lo primero
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {byDay.map(([day, rs]) => (
            <section key={day} data-testid={`movements-day-${day}`}>
              <h2 className="text-[13px] uppercase tracking-[0.14em] text-slate-500 font-bold mb-2.5">
                {dayHeading(day)}
              </h2>
              <ul className="space-y-1">
                {rs.map(r => <MovementRow key={r.id} row={r} onEdit={() => r.tx && setEditTx(r.tx)} />)}
              </ul>
            </section>
          ))}
        </div>
      )}

      {editTx && (
        <EditTransactionDialog tx={editTx} accounts={accounts}
          onClose={() => setEditTx(null)}
          onSaved={() => { setEditTx(null); load(); }} />
      )}
    </div>
  );
}

function MovementRow({ row, onEdit }) {
  const look = LOOK[row.group] || LOOK.out;
  const Icon = look.icon;
  const clickable = !!row.tx;
  const Tag = clickable ? "button" : "div";
  return (
    <li>
      <Tag
        {...(clickable ? { onClick: onEdit, "data-testid": `movement-${row.id}` } : { "data-testid": `movement-${row.id}` })}
        className={`w-full flex items-center gap-3 p-3 rounded-2xl text-left min-h-16 ${
          clickable ? "hover:bg-white/[0.05]" : ""}`}>
        <span className="w-10 h-10 rounded-xl bg-white/[0.06] flex items-center justify-center shrink-0">
          <Icon className={`w-[18px] h-[18px] ${look.cls}`} aria-hidden="true" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[15px] font-medium text-white truncate">{row.title}</span>
          <span className="block text-[13px] text-slate-400 truncate">
            {row.group === "move"
              ? `${row.from || "?"} → ${row.to || "?"}`
              : [row.category, row.where].filter(Boolean).join(" · ")}
          </span>
        </span>
        <span className={`text-[15px] font-semibold tabular-nums shrink-0 ${look.cls}`}>
          {look.sign}{money(row.amount, row.currency)}
        </span>
      </Tag>
    </li>
  );
}
